import { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import debounce from 'lodash/debounce'
import React, { useCallback, useMemo, useRef, useEffect } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { semiFormToObject } from 'lib/forms/formUtils'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import CruisePriceFilter, {
  CruisePriceFilterChangeProps,
} from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePriceFilter'
import CruiseShipFilter
  from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseShipFilter'
import CruisePromotionFilter
  from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePromotionFilter'
import CruiseCabinTypeFilter
  from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseCabinTypeFilter'
import { useIsMobileScreen } from 'lib/web/deviceUtils'

const isLuxuryEscapes = config.BRAND === LUXURY_ESCAPES

export function buildPanelCheckItems(
  facets: Array<App.CruiseSearchFacet>,
  searchString?: string,
) {
  if (facets.length === 0) return []

  return facets.map((item): FilterPanelCheckItem => ({
    value: item.name,
    hidden: !!(searchString && facets.length > 5 && !item.name.toLowerCase().includes(searchString.toLowerCase())),
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {item.name}
        <span>({(item.count || 0) + (item.flashOfferCount || 0)})</span>
      </Group>
    ),
  }))
}

interface Props {
  listFilters: App.OfferListFilters;
  onChange?: (values: Record<string, any>) => void;
}

function CruiseSearchFiltersInputs(props: Props) {
  const {
    listFilters,
    onChange = noop,
  } = props

  const containerRef = useRef<HTMLDivElement>()
  const queryParams = useQueryParams()
  const isMobile = useIsMobileScreen()

  const filters = useMemo(() => {
    const luxExclusiveValue = queryParams.get('isLuxExclusivePromotion')
    const priceMin = queryParams.get('minPrice')
    const priceMax = queryParams.get('maxPrice')
    const cabinTypes = queryParams.get('cabinTypes') || undefined

    return {
      price: {
        min: priceMin ? parseInt(priceMin) : undefined,
        max: priceMax ? parseInt(priceMax) : undefined,
      },
      cabinTypes,
      cruiseShips: new Set(queryParams.getAll('cruiseShips')),
      promotions: new Set(queryParams.getAll('promotions')),
      isSpecialOffers: queryParams.get('isSpecialOffers') ? new Set(['Special offers']) : null,
      isLuxExclusivePromotion: luxExclusiveValue && isLuxuryEscapes ? new Set(['Lux Exclusive']) : null,
    }
  }, [queryParams])

  const mappedFilters = useMemo(() => {
    return {
      ...filters,
      minPrice: filters.price.min,
      maxPrice: filters.price.max,
      isLuxExclusivePromotion: filters?.isLuxExclusivePromotion?.size! > 0,
      isSpecialOffers: filters?.isSpecialOffers?.size! > 0,
      cruiseShips: Array.from(filters.cruiseShips),
      promotions: Array.from(filters.promotions),
    }
  }, [filters])

  const facetParams = useMemo(() => ({
    facetTypes: ['ship_names', 'cabin_types', 'special_offers', 'lux_exclusive', 'cruise_prices'],
    cruiseLines: listFilters.cruiseLines,
    departurePlaceId: listFilters.departurePlaceId,
    departureIds: listFilters.departureIds,
    rateCodes: listFilters.rateCodes,
    destinationId: listFilters.destinationId,
    destinationIds: listFilters.destinationIds,
    departureMonths: listFilters.departureMonths,
    durationMin: listFilters.durationMin,
    durationMax: listFilters.durationMax,
    departureStartDate: listFilters.departureStartDate,
    departureEndDate: listFilters.departureEndDate,
    durationRange: listFilters.durationRange,
    ...mappedFilters,
  } as App.CruiseSearchFacetParams), [listFilters.cruiseLines, listFilters.departurePlaceId, listFilters.departureIds, listFilters.rateCodes, listFilters.destinationId, listFilters.destinationIds, listFilters.departureMonths, listFilters.durationMin, listFilters.durationMax, listFilters.departureStartDate, listFilters.departureEndDate, listFilters.durationRange, mappedFilters])

  const [facets, fetchingFacets] = useCruiseSearchFacets(facetParams)

  const getFormValues = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current)
    delete formValues.price

    if (formValues.isLuxExclusivePromotion) {
      formValues.isLuxExclusivePromotion = true
    }

    if (formValues.isSpecialOffers) {
      formValues.isSpecialOffers = true
    }

    return formValues
  }, [])

  const onFilterChange = useCallback(() => {
    const formValues = getFormValues()
    onChange(formValues)
  }, [onChange, getFormValues])

  const onResetPrices = useCallback(() => {
    const formValues = getFormValues()
    onChange({ ...formValues, minPrice: undefined, maxPrice: undefined })
  }, [onChange, getFormValues])

  const onResetCabinTypes = useCallback(() => {
    const formValues = getFormValues()
    delete formValues.cabinTypes
    onChange(formValues)
  }, [onChange, getFormValues])

  const onDelayedFilterChange = useMemo(() => debounce(onFilterChange, 500), [onFilterChange])

  const onPriceFilterChange = useCallback((priceFilters: CruisePriceFilterChangeProps) => {
    const formValues = getFormValues()
    onChange({ ...formValues, ...priceFilters })
  }, [getFormValues, onChange])

  // clean ships selected if not in facets
  useEffect(() => {
    const shipsSelected = listFilters.cruiseShips || []
    const shipsInFacets = shipsSelected.filter((ship) => facets.find((facet) => facet.category === 'ship_names' && facet.name === ship))

    if (shipsSelected.length !== shipsInFacets.length && !fetchingFacets) {
      const isLuxExclusivePromotion = listFilters.isLuxExclusivePromotion
      const isSpecialOffers = listFilters.isSpecialOffers

      onChange({
        ...(isLuxExclusivePromotion && { isLuxExclusivePromotion }),
        ...(isSpecialOffers && { isSpecialOffers }),
        cabinTypes: listFilters.cabinTypes,
        cruiseShips: shipsInFacets,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    facets,
    listFilters.cruiseShips,
    onChange,
    fetchingFacets,
  ])

  return <VerticalSpacer gap={24} ref={containerRef}>
    {fetchingFacets && <>
      <FilterPanelCheckboxLoader />
      <FilterPanelCheckboxLoader />
      {isMobile && <FilterPanelCheckboxLoader />}
    </>}
    {(!fetchingFacets || isMobile) && <>
      <FilterPanelFilterGroup
        title="Price"
        canReset={!!filters.price.min || !!filters.price.max}
        onReset={onResetPrices}>
        <CruisePriceFilter
          filters={mappedFilters}
          onChange={onPriceFilterChange}
          facets={facets}/>
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        title="Cruise ships"
        canReset={filters.cruiseShips.size > 0}
        onReset={onFilterChange}>
        <CruiseShipFilter
          filters={filters}
          facets={facets}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        title="Cabin types"
        canReset={!!filters.cabinTypes}
        onReset={onResetCabinTypes}
      >
        <CruiseCabinTypeFilter
          filters={filters}
          facets={facets}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>
      <FilterPanelFilterGroup
        title="Promotions"
        canReset={filters.promotions.size > 0}
        onReset={onFilterChange}
      >
        <CruisePromotionFilter
          facets={facets}
          filters={filters}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>
    </>}
  </VerticalSpacer>
}

export default CruiseSearchFiltersInputs
